import { JSONResponse } from '../../Types/Types'

/**
 * Attempts to execute a promise-returning function.
 * If the promise is rejected, retries the operation up to `maxRetries` times,
 * waiting `delay` milliseconds between each attempt.
 *
 * @param promise - A function that returns a Promise resolving to JSONResponse<T>.
 * @param maxRetries - Maximum number of retry attempts (default: 900).
 * @param delay - Delay in milliseconds between retries (default: 1000).
 * @param attempt - Current attempt count (default: 0, used internally).
 * @returns A Promise resolving to JSONResponse<T> if successful, or rejects after all retries fail.
 */
export const catchAndTryAgainPromise = async <T extends unknown>(
    promise: () => Promise<JSONResponse<T>>,
    maxRetries = 900,
    delay = 1000,
    attempt = 0
): Promise<JSONResponse<T>> =>
    promise().catch((e: Error): Promise<JSONResponse<T>> => {
        if (attempt < maxRetries) {
            return new Promise<JSONResponse<T>>((resolve): void => {
                setTimeout((): void => {
                    resolve(catchAndTryAgainPromise(promise, maxRetries, delay, attempt + 1))
                }, delay)
            })
        }
        return Promise.reject(e)
    })
